import { useState, useEffect, useRef } from 'react';
import type { Explanation, Question } from '../types';
import apiClient from '../auth/service';
import { type UiLang, optionsToPlain, explanationToPlain, displaySourceSig } from '../utils/displayTranslation';

type Bundle = { text: string; options: Record<string, string>; explanation: Explanation };

function readCache(id: number, lang: UiLang, sig: string): Bundle | null {
    if (typeof sessionStorage === 'undefined') return null;
    try {
        const raw = sessionStorage.getItem(`simulado-disp:${id}:${lang}`);
        if (!raw) return null;
        const p = JSON.parse(raw) as { sig?: string } & Bundle;
        if (p.sig !== sig) return null;
        return { text: p.text, options: p.options, explanation: p.explanation };
    } catch {
        return null;
    }
}

function writeCache(id: number, lang: UiLang, sig: string, b: Bundle): void {
    if (typeof sessionStorage === 'undefined') return;
    try {
        sessionStorage.setItem(`simulado-disp:${id}:${lang}`, JSON.stringify({ sig, ...b }));
    } catch {
        /* quota */
    }
}

/**
 * Texto das questões/alternativas/explicação para exibição em EN/ES.
 * Origem continua em PT no banco; respostas enviadas seguem chaves A/B/C.
 * Cache só em sessionStorage (não persiste tradução no Mongo).
 */
export function useDisplayTranslation(
    question: Question,
    displayLanguage: UiLang
): { stem: string; options: Record<string, string>; explanation: Explanation; loading: boolean } {
    const qRef = useRef(question);
    qRef.current = question;
    const sig = displaySourceSig(question);

    const [stem, setStem] = useState(question.text);
    const [options, setOptions] = useState<Record<string, string>>(() => optionsToPlain(question.options));
    const [explanation, setExplanation] = useState<Explanation>(question.explanation);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const q = qRef.current;

        if (displayLanguage === 'pt') {
            setStem(q.text);
            setOptions(optionsToPlain(q.options));
            setExplanation(q.explanation);
            setLoading(false);
            return;
        }

        const target = displayLanguage as 'en' | 'es';
        const cached = readCache(q.id, displayLanguage, sig);
        if (cached) {
            setStem(cached.text);
            setOptions(cached.options);
            setExplanation(cached.explanation);
            setLoading(false);
            return;
        }

        setLoading(true);
        (async () => {
            const cur = qRef.current;
            try {
                const res = (await apiClient.post('/translate-display', {
                    text: cur.text,
                    options: optionsToPlain(cur.options),
                    explanation: explanationToPlain(cur.explanation),
                    targetLang: target,
                })) as Bundle;
                if (cancelled) return;
                setStem(res.text);
                setOptions(res.options);
                setExplanation(res.explanation);
                writeCache(cur.id, displayLanguage, sig, res);
            } catch (e) {
                if (cancelled) return;
                console.warn('[translate-display] falhou, texto em português:', e);
                const fb = qRef.current;
                setStem(fb.text);
                setOptions(optionsToPlain(fb.options));
                setExplanation(fb.explanation);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [question.id, displayLanguage, sig]);

    return { stem, options, explanation, loading };
}
